export function peopleFilter(config = {}) {
  return {
    open: false,
    activeTab: config.showGroups && config.hasGroups ? "groups" : "teams",
    hasGroups: config.hasGroups,
    snapshotId: config.snapshotId,

    // Team data - using tree structure from squadMultiselect
    selectedTeamIds: new Set(),
    childOfSelectedIds: new Set(),
    parentOfSelectedIds: new Set(),
    squads: null,
    allSquads: null,
    rootIds: null,
    userSquadIds: new Set(config.userSquadIds || []),
    teamViewTab: "all_teams",

    // Group data
    groups: config.groups || [],
    selectedGroupIds: new Set(),
    userGroupIds: new Set(config.userGroupIds || []),
    groupSearchTerm: "",
    groupViewTab: "all_groups",

    init() {
      // Initialize selections from store state first, then fallback to URL params
      // This ensures the UI reflects current filter state when navigating between tabs
      const store = this.$store?.snapshotResultFilters;
      let hasStoreState = false;

      if (store) {
        // Read from store first
        if (store.groupIds && store.groupIds.length > 0) {
          this.selectedGroupIds = new Set(store.groupIds);
          if (this.hasGroups) this.activeTab = "groups";
          hasStoreState = true;
        }

        if (store.squadIds && store.squadIds.length > 0) {
          this.selectedTeamIds = new Set(store.squadIds);
          if (!hasStoreState) this.activeTab = "teams";
          hasStoreState = true;
        }

        // Set active tab based on store filterType if available
        if (store.filterType && this.hasGroups && hasStoreState) {
          this.activeTab = store.filterType;
        }
      }

      // Only read from URL params if we don't have store state
      if (!hasStoreState) {
        const urlGroupIds = new URLSearchParams(window.location.search).get(
          "group_ids",
        );
        const urlSquadIds = new URLSearchParams(window.location.search).get(
          "squad_ids",
        );

        if (urlGroupIds) {
          const groupIds = urlGroupIds
            .split(",")
            .map((id) => parseInt(id))
            .filter(Boolean);
          this.selectedGroupIds = new Set(groupIds);
          if (this.hasGroups) this.activeTab = "groups";
        }

        if (urlSquadIds) {
          const squadIds = urlSquadIds
            .split(",")
            .map((id) => parseInt(id))
            .filter((id) => !isNaN(id) && id > 0);
          this.selectedTeamIds = new Set(squadIds);
          if (!urlGroupIds) this.activeTab = "teams";
        }
      }

      // Listen for squadsTree initialization
      this.$root.addEventListener("dx:squadsTree:init", (e) => {
        this.squads = e.detail;
        this.childOfSelectedIds = this.initializeChildOfSelectedIds(
          this.selectedTeamIds,
          this.squads,
        );
        this.parentOfSelectedIds = this.initializeParentOfSelectedIds(
          this.selectedTeamIds,
          this.squads,
        );
        this.rootIds = new Set(
          this.squads.filter((s) => s.depth == 0).map((s) => s.id),
        );
      });

      // Watch for store changes
      this.$watch("$store.snapshotResultFilters.squadIds", (newSquadIds) => {
        this.selectedTeamIds = new Set(newSquadIds || []);
        if (this.squads) {
          this.childOfSelectedIds = this.initializeChildOfSelectedIds(
            this.selectedTeamIds,
            this.squads,
          );
          this.parentOfSelectedIds = this.initializeParentOfSelectedIds(
            this.selectedTeamIds,
            this.squads,
          );
        }
      });

      this.$watch("$store.snapshotResultFilters.groupIds", (newGroupIds) => {
        this.selectedGroupIds = new Set(newGroupIds || []);
      });

      this.$watch("$store.snapshotResultFilters.filterType", (newType) => {
        if (newType && this.hasGroups) {
          this.activeTab = newType;
        }
      });
    },

    // Getters
    get currentButtonLabel() {
      if (this.activeTab === "teams") {
        return "Teams";
      } else {
        return "Groups";
      }
    },

    get hasSelections() {
      if (this.activeTab === "teams") {
        return this.selectedTeamIds.size > 0;
      } else {
        return this.selectedGroupIds.size > 0;
      }
    },

    get selectionText() {
      if (this.activeTab === "teams") {
        const count = this.selectedTeamIds.size + this.childOfSelectedIds.size;
        return window.pluralizeWithCount("team", count);
      } else {
        const count = this.selectedGroupIds.size;
        return window.pluralizeWithCount("group", count);
      }
    },

    get queryParams() {
      const params = new URLSearchParams(window.location.search);

      // Remove existing filters
      params.delete("squad_ids");
      params.delete("group_ids");

      if (this.activeTab === "teams" && this.selectedTeamIds.size > 0) {
        params.set("squad_ids", Array.from(this.selectedTeamIds).join(","));
      } else if (
        this.activeTab === "groups" &&
        this.selectedGroupIds.size > 0
      ) {
        params.set("group_ids", Array.from(this.selectedGroupIds).join(","));
      }

      return params.toString();
    },

    // Team-related getters and methods
    get filteredSquads() {
      if (!this.squads) return [];

      if (this.teamViewTab === "your_teams" && this.userSquadIds.size > 0) {
        // Return only squads where user is member/DRI
        return this.squads.filter((s) => this.userSquadIds.has(s.id));
      }

      // Return all squads
      return this.squads;
    },

    // Group-related getters and methods
    get filteredGroups() {
      let baseGroups = this.groups;

      // Filter by view tab
      if (this.groupViewTab === "your_groups") {
        baseGroups = baseGroups.filter((g) => this.userGroupIds.has(g.id));
      }

      // Filter by search term
      if (this.groupSearchTerm) {
        return baseGroups.filter((group) =>
          group.name.toLowerCase().includes(this.groupSearchTerm.toLowerCase()),
        );
      }

      return baseGroups;
    },

    // Methods
    toggleDropdown() {
      if (this.open) {
        return this.handleBlur();
      }

      this.open = true;
      this.$nextTick(() => {
        if (this.activeTab === "teams") {
          this.$refs.teamSearchInput?.focus();
        } else {
          this.$refs.groupSearchInput?.focus();
        }
      });
    },

    handleBlur() {
      if (!this.open) return;
      this.open = false;
    },

    switchTab(tab) {
      this.activeTab = tab;
      // Update store filter type
      if (this.$store) {
        this.$store.snapshotResultFilters.filterType = tab;
      }

      this.$nextTick(() => {
        if (tab === "teams") {
          this.$refs.teamSearchInput?.focus();
        } else {
          this.$refs.groupSearchInput?.focus();
        }
      });
    },

    // Tree-based team selection methods (from squadMultiselect)
    isSelected(id) {
      return this.selectedTeamIds.has(id);
    },

    isChildOfSelectedId(id) {
      return this.childOfSelectedIds.has(id);
    },

    isParentOfSelectedId(id) {
      return this.parentOfSelectedIds.has(id);
    },

    isIndeterminate(id) {
      return !this.isSelected(id) && this.isParentOfSelectedId(id);
    },

    isDisabled(id) {
      return this.childOfSelectedIds.has(id);
    },

    select(id) {
      if (this.childOfSelectedIds.has(id)) return;

      const selected = this.isSelected(id);
      const childIds = this.getChildIds(id, this.squads);

      if (selected) {
        // De-select team
        this.selectedTeamIds.delete(id);
        childIds.forEach((cid) => this.childOfSelectedIds.delete(cid));
      }

      if (!selected) {
        // Select team
        this.selectedTeamIds.add(id);
        childIds.forEach((cid) => {
          this.childOfSelectedIds.add(cid);
          this.selectedTeamIds.delete(cid);
        });
      }

      this.parentOfSelectedIds = this.initializeParentOfSelectedIds(
        this.selectedTeamIds,
        this.squads,
      );
      this.emitTeamChange();
    },

    clearTeamSelections() {
      this.selectedTeamIds.clear();
      this.childOfSelectedIds.clear();
      this.parentOfSelectedIds.clear();
      this.emitTeamChange();
    },

    // Group selection methods
    toggleGroupSelection(groupId) {
      if (this.selectedGroupIds.has(groupId)) {
        this.selectedGroupIds.delete(groupId);
      } else {
        this.selectedGroupIds.add(groupId);
      }
      this.emitGroupChange();
    },

    clearGroupSelections() {
      this.selectedGroupIds.clear();
      this.emitGroupChange();
    },

    searchGroups() {
      const term = this.$refs.groupSearchInput.value.toLowerCase();
      this.groupSearchTerm = term;
    },

    // Event emission methods
    emitTeamChange() {
      const selectedIds = Array.from(this.selectedTeamIds);

      // Update store directly
      if (this.$store) {
        this.$store.snapshotResultFilters.squadIds = [...selectedIds];
        this.$store.snapshotResultFilters.groupIds = [];
        this.$store.snapshotResultFilters.filterType = "teams";
      }

      // Also dispatch event for any external listeners
      this.$dispatch("change-squad-ids", selectedIds);
    },

    emitGroupChange() {
      const selectedIds = Array.from(this.selectedGroupIds);

      // Update store directly
      if (this.$store) {
        this.$store.snapshotResultFilters.groupIds = [...selectedIds];
        this.$store.snapshotResultFilters.squadIds = [];
        this.$store.snapshotResultFilters.filterType = "groups";
      }

      // Also dispatch event for any external listeners
      this.$dispatch("change-group-ids", selectedIds);
    },

    // Helper functions (from squadMultiselect) - IMPORTANT: Keep in sync with squadMultiselect.js
    getChildIds(parentId, squads) {
      const childIds = [];
      let parent = null;

      squads.forEach((squad) => {
        if (parentId == squad.id) {
          parent = squad;
          return;
        }

        if (parent && parent.depth < squad.depth) {
          childIds.push(squad.id);
        }

        if (parent && parent.depth >= squad.depth) {
          parent = null;
        }
      });

      return childIds;
    },

    initializeChildOfSelectedIds(selectedIds, squads) {
      const childOfSelectedIds = new Set();
      let parent = null;

      squads.forEach((squad) => {
        if (selectedIds.has(squad.id)) {
          parent = squad;
          return;
        }

        if (parent && parent.depth < squad.depth) {
          childOfSelectedIds.add(squad.id);
        }

        if (parent && parent.depth >= squad.depth) {
          parent = null;
        }
      });

      return childOfSelectedIds;
    },

    initializeParentOfSelectedIds(selectedIds, squads) {
      const parentIdMap = Object.fromEntries(
        squads.map((squad) => [squad.id, squad.parent_id]),
      );

      const parentOfSelectedIds = new Set();

      selectedIds.forEach((selectedId) => {
        let parentId = parentIdMap[selectedId];
        while (parentId) {
          parentOfSelectedIds.add(parentId);
          parentId = parentIdMap[parentId];
        }
      });

      return parentOfSelectedIds;
    },
  };
}
